/**
 * The binary AWS event-stream framing that Amazon Transcribe Streaming speaks
 * over its WebSocket (UC-15, "Say the value"):
 *
 *   [total length u32][headers length u32][prelude CRC32]
 *   [headers …][payload …][message CRC32]
 *
 * A header is [name length u8][name][type u8 = 7 (string)][value length u16][value].
 * Only what the guided session needs: encoding audio events, decoding events.
 */

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

export function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) crc = CRC_TABLE[(crc ^ bytes[i]!) & 0xff]! ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

function encodeHeaders(headers: Record<string, string>): Uint8Array {
  const parts: number[] = [];
  for (const [name, value] of Object.entries(headers)) {
    const n = encoder.encode(name);
    const v = encoder.encode(value);
    parts.push(n.length, ...n, 7, (v.length >> 8) & 0xff, v.length & 0xff, ...v);
  }
  return Uint8Array.from(parts);
}

export function encodeMessage(headers: Record<string, string>, payload: Uint8Array): Uint8Array {
  const headerBytes = encodeHeaders(headers);
  const total = 12 + headerBytes.length + payload.length + 4;
  const out = new Uint8Array(total);
  const view = new DataView(out.buffer);
  view.setUint32(0, total);
  view.setUint32(4, headerBytes.length);
  view.setUint32(8, crc32(out.subarray(0, 8)));
  out.set(headerBytes, 12);
  out.set(payload, 12 + headerBytes.length);
  view.setUint32(total - 4, crc32(out.subarray(0, total - 4)));
  return out;
}

/** One chunk of 16-bit little-endian PCM as an AudioEvent (an empty chunk ends the stream). */
export function encodeAudioEvent(pcm: Uint8Array): Uint8Array {
  return encodeMessage({ ':content-type': 'application/octet-stream', ':event-type': 'AudioEvent', ':message-type': 'event' }, pcm);
}

export interface DecodedMessage {
  headers: Record<string, string>;
  payload: Uint8Array;
}

/** Decodes one message; throws on a broken frame (wrong length or CRC). */
export function decodeMessage(bytes: Uint8Array): DecodedMessage {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const total = view.getUint32(0);
  const headersLength = view.getUint32(4);
  if (total !== bytes.length || total < 16) throw new Error('event-stream: bad length');
  if (view.getUint32(8) !== crc32(bytes.subarray(0, 8))) throw new Error('event-stream: bad prelude CRC');
  if (view.getUint32(total - 4) !== crc32(bytes.subarray(0, total - 4))) throw new Error('event-stream: bad message CRC');
  const headers: Record<string, string> = {};
  let i = 12;
  const end = 12 + headersLength;
  while (i < end) {
    const nameLength = bytes[i]!;
    const name = decoder.decode(bytes.subarray(i + 1, i + 1 + nameLength));
    i += 1 + nameLength;
    const type = bytes[i]!;
    i += 1;
    if (type !== 7) throw new Error('event-stream: unsupported header type');
    const valueLength = view.getUint16(i);
    headers[name] = decoder.decode(bytes.subarray(i + 2, i + 2 + valueLength));
    i += 2 + valueLength;
  }
  return { headers, payload: bytes.subarray(end, total - 4) };
}

/** Float32 samples at `fromRate` → 16-bit PCM (little endian) at `toRate`, by averaging. */
export function toPcm16(samples: Float32Array, fromRate: number, toRate: number): Uint8Array {
  const ratio = fromRate / toRate;
  const length = Math.floor(samples.length / ratio);
  const out = new DataView(new ArrayBuffer(length * 2));
  for (let i = 0; i < length; i++) {
    const start = Math.floor(i * ratio);
    const stop = Math.min(samples.length, Math.floor((i + 1) * ratio));
    let sum = 0;
    for (let j = start; j < stop; j++) sum += samples[j]!;
    const v = Math.max(-1, Math.min(1, sum / Math.max(1, stop - start)));
    out.setInt16(i * 2, v < 0 ? v * 0x8000 : v * 0x7fff, true);
  }
  return new Uint8Array(out.buffer);
}
